/**
 * Which QC hub tabs NTX sees — the policy, with no dependency on the module
 * barrel so it stays unit-testable (the barrel pulls MUI icons, which don't
 * resolve under vitest's node environment). Same shape as
 * `inventoryTabPolicy.ts`, for the same reason.
 *
 * ONE YARD. @intrasync/quality-control ships its own parallel yard — a "Yard"
 * tab backed by `qc_yard_locations` / `qc_product_locations`, with its own
 * "Place Product" action — which knows nothing about
 * @intrasync/yard-management's `yard_assets` and is invisible to it. Two
 * screens, both called "the yard", holding different pieces.
 *
 * NTX's instruction (2026-08-31) is that yard-management is the only usable
 * yard, and the QC module is the only usable QC. So the QC hub's Yard tab is
 * hidden here. Nothing is unregistered: `qc-yard-locations` and
 * `qc-product-locations` stay mounted and the hub Body still resolves the panel
 * if something navigates to it, so nothing can strand on a blank screen.
 *
 * Reverse this list — not the module — if NTX later asks for it.
 */

/** The hub opens on this tab, so it must stay visible or the strip has no selection. */
export const DEFAULT_TAB_ID = "dashboard";

/** Tabs NTX does NOT see, and why. */
export const NTX_HIDDEN_TAB_IDS: readonly string[] = [
  "yard", // duplicate of @intrasync/yard-management — see the note above
];

/**
 * A capability NTX grants to nobody.
 *
 * Setting `visible = false` on the tab is enough only while the host installs
 * no `rootStore.qcPermissions`: `resolveTabVisibility` passes the flag through
 * untouched in that case, but RECOMPUTES it from the permission set when one
 * exists. So a future session wiring QC permissions would silently bring the
 * Yard tab back. Re-pointing the tab's required capability at a grant that is
 * never issued closes both paths.
 */
export const NEVER_GRANTED_CAPABILITY = "qc:yard-disabled-at-ntx";

const HIDDEN = new Set(NTX_HIDDEN_TAB_IDS);

export const isQcTabVisible = (tabId: string): boolean => !HIDDEN.has(tabId);
